import { ORG_TIPO } from "./schema.js";
import type { StatusOrg } from "./org-status.js";

/**
 * FAZENDAS na Plataforma (P1) — o DTO e as regras puras, sem React e sem drizzle,
 * para o servidor (server/lib/plataforma/orgs.ts), o client
 * (client/src/pages/plataforma/*) e o teste lerem a MESMA coisa.
 */

/** Uma fazenda (organização) na lista e na ficha. */
export interface FazendaDTO {
  id: string;
  nome: string;
  nomeFantasia: string | null;
  slug: string;
  tipo: string;
  perfilComprador: string | null;
  cidade: string | null;
  cnpjCpf: string | null;
  telefoneWhatsapp: string | null;
  praca: string | null;
  pracas: string[];
  plano: string;
  modulesEnabled: string[];
  ativo: boolean;
  trialEndsAt: string | null;
  trialDiasRestantes: number | null;
  hasSubscription: boolean;
  /** SIGNUP | CORTESIA — por que `trialEndsAt` está como está. */
  origemTrial: string;
  observacoesPlataforma: string | null;
  status: StatusOrg;
  usuariosTotal: number;
  usuariosAtivos: number;
  /** MAX(ultimo_login_em) — `null` = ninguém entrou ainda. */
  ultimoAcesso: string | null;
  createdAt: string;
}

/** Um usuário na ficha da fazenda — nunca a senha. */
export interface UsuarioDaFazendaDTO {
  id: string;
  name: string;
  email: string | null;
  telefone: string | null;
  role: string;
  ativo: boolean;
  plataformaAdmin: boolean;
  modulos: string[] | null;
  funcionarioId: string | null;
  ultimoLoginEm: string | null;
  createdAt: string;
}

export interface AuditDaFazendaDTO {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface FichaFazendaDTO extends FazendaDTO {
  usuarios: UsuarioDaFazendaDTO[];
  auditoria: AuditDaFazendaDTO[];
}

export interface VisaoGeralDTO {
  fazendas: { total: number; porStatus: Record<StatusOrg, number>; porTipo: Record<string, number> };
  trialsVencendo7d: number;
  semAcesso30d: number;
  corretoresAtivos: number;
  /** Pedidos de acesso de suporte aguardando o admin da org (P2). */
  acessosPendentes: number;
}

export interface PatchFazenda {
  nome?: string;
  tipo?: string;
  cnpjCpf?: string | null;
  telefoneWhatsapp?: string | null;
  praca?: string | null;
  cidade?: string | null;
  observacoesPlataforma?: string | null;
  modulesEnabled?: string[];
  ativo?: boolean;
}

export interface CortesiaInput {
  /** `null` = permanente · ISO = até a data · 'revoke' = revoga (volta ao trial vencido) */
  trialEndsAt: string | null;
  modules?: string[];
}

export interface FiltroFazendas { tipo?: string; status?: string; q?: string }

function comparavel(v: string): string {
  return v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** O filtro da lista — o mesmo no servidor (`?tipo=&status=&q=`) e no client. */
export function filtrarFazendas(lista: FazendaDTO[], filtro: FiltroFazendas): FazendaDTO[] {
  const q = comparavel((filtro.q ?? "").trim());
  return lista.filter(f => {
    if (filtro.tipo && filtro.tipo !== "todos" && f.tipo !== filtro.tipo) return false;
    if (filtro.status && filtro.status !== "todos" && f.status !== filtro.status) return false;
    if (!q) return true;
    return comparavel(f.nome).indexOf(q) >= 0
      || comparavel(f.nomeFantasia ?? "").indexOf(q) >= 0
      || comparavel(f.slug).indexOf(q) >= 0
      || comparavel(f.cidade ?? "").indexOf(q) >= 0;
  });
}

/** Ordem: quem precisa de atenção primeiro (trial vencido, inativa), depois por nome. */
export function ordenarFazendas(lista: FazendaDTO[]): FazendaDTO[] {
  const peso: Record<StatusOrg, number> = { TRIAL_EXPIRADO: 0, INATIVA: 1, TRIAL: 2, CORTESIA: 3, ASSINANTE: 3 };
  return lista.slice().sort((a, b) => {
    if (peso[a.status] !== peso[b.status]) return peso[a.status] - peso[b.status];
    return a.nome.localeCompare(b.nome, "pt-BR");
  });
}

/**
 * Os módulos que um TIPO de org não pode perder: o PRODUTOR tem plantação (Cacho
 * é a base — a 055 garante isso em toda org produtora), o COMPRADOR só existe
 * pelo Mercado. Sem isto a lista de módulos ficaria vazia ou sem o que dá
 * sentido à org, e a raiz do app mostraria "nenhum módulo".
 */
export function moduloObrigatorioDoTipo(tipo: string): string | null {
  if (tipo === ORG_TIPO.PRODUTOR) return "cacho";
  if (tipo === ORG_TIPO.COMPRADOR) return "mercado";
  return null;
}

export function erroDosModulos(tipo: string, modulesEnabled: string[]): { code: string; mensagem: string } | null {
  const obrigatorio = moduloObrigatorioDoTipo(tipo);
  if (obrigatorio && modulesEnabled.indexOf(obrigatorio) < 0) {
    return {
      code: "MODULO_OBRIGATORIO",
      mensagem: tipo === ORG_TIPO.PRODUTOR
        ? "Uma fazenda produtora não pode ficar sem o Cacho."
        : "Um comprador não pode ficar sem o Mercado.",
    };
  }
  return null;
}

export const ROTULO_ORIGEM_TRIAL: Record<string, string> = {
  SIGNUP: "Cadastro",
  CORTESIA: "Cortesia do Rizoma",
};
